/**
 * Shared formatting / lookup helpers for the Fixed Asset Management pages.
 *
 * Centralised here (rather than repeated per page, the way older procurement
 * pages do it) so a null-vs-zero decision or a status label only has to be
 * gotten right once. See erp-api/src/models/Asset.js and
 * erp-api/src/services/depreciationService.js for the source of truth on
 * every enum below — do not invent a status or label that isn't there.
 */
import { formatCurrency } from '../../utils/currency';
import type { StatusTone } from '../../components/common/pageKit';
import type {
  AssetStatus,
  MaintenanceType,
  DepreciationMethod,
  BlockClass,
  DepreciationRunStatus,
  DisposalType,
  DepreciationBook,
} from '../../services/assetService';

// ---------------------------------------------------------------------------
// Numbers / dates — null is never rendered as zero.
// ---------------------------------------------------------------------------

/** DECIMAL columns arrive as strings over the wire (see assetService.ts header). */
export const toNum = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

export const fmtMoney = (v: number | string | null | undefined): string => {
  const n = toNum(v);
  return n === null ? '—' : formatCurrency(n);
};

export const fmtNum = (v: number | string | null | undefined, digits = 0): string => {
  const n = toNum(v);
  return n === null ? '—' : n.toLocaleString('en-IN', { maximumFractionDigits: digits });
};

export const fmtPct = (v: number | string | null | undefined, digits = 2): string => {
  const n = toNum(v);
  return n === null ? '—' : `${n.toFixed(digits)}%`;
};

export const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Net book value = gross block - accumulated depreciation. Both are real
 *  columns on every asset row, so this is never null for a fetched asset —
 *  callers still route it through fmtMoney for consistent em-dash handling
 *  on a malformed row. */
export const netBookValue = (grossBlock: number | string, accumulatedDepreciation: number | string): number | null => {
  const g = toNum(grossBlock);
  const a = toNum(accumulatedDepreciation);
  if (g === null || a === null) return null;
  return g - a;
};

export const assigneeName = (
  first: string | null | undefined,
  last: string | null | undefined
): string | null => {
  const name = [first, last].filter(Boolean).join(' ').trim();
  return name || null;
};

// ---------------------------------------------------------------------------
// Asset status
// ---------------------------------------------------------------------------

export const ASSET_STATUS_LABEL: Record<AssetStatus, string> = {
  IN_USE: 'In Use',
  IN_STORE: 'In Store',
  UNDER_REPAIR: 'Under Repair',
  RETIRED: 'Retired',
  DISPOSED: 'Disposed',
};

export const ASSET_STATUS_TONE: Record<AssetStatus, StatusTone> = {
  IN_USE: 'success',
  IN_STORE: 'info',
  UNDER_REPAIR: 'warning',
  RETIRED: 'neutral',
  DISPOSED: 'error',
};

// ---------------------------------------------------------------------------
// Maintenance
// ---------------------------------------------------------------------------

export const MAINTENANCE_TYPE_LABEL: Record<MaintenanceType, string> = {
  PREVENTIVE: 'Preventive',
  CORRECTIVE: 'Corrective',
  CALIBRATION: 'Calibration',
  INSPECTION: 'Inspection',
};

// ---------------------------------------------------------------------------
// Depreciation
// ---------------------------------------------------------------------------

export const DEPRECIATION_METHOD_LABEL: Record<DepreciationMethod, string> = {
  SLM: 'Straight Line (SLM)',
  WDV: 'Written Down Value (WDV)',
  NONE: 'Not Depreciated',
};

export const DEPRECIATION_BOOK_LABEL: Record<DepreciationBook, string> = {
  COMPANIES_ACT: 'Companies Act (Schedule II)',
  INCOME_TAX: 'Income Tax (Block of Assets)',
};

export const RUN_STATUS_LABEL: Record<DepreciationRunStatus, string> = {
  DRAFT: 'Draft',
  POSTED: 'Posted',
  REVERSED: 'Reversed',
};

export const RUN_STATUS_TONE: Record<DepreciationRunStatus, StatusTone> = {
  DRAFT: 'warning',
  POSTED: 'success',
  REVERSED: 'neutral',
};

// ---------------------------------------------------------------------------
// Income Tax blocks
// ---------------------------------------------------------------------------

export const BLOCK_CLASS_LABEL: Record<BlockClass, string> = {
  BUILDING: 'Building',
  PLANT_MACHINERY: 'Plant & Machinery',
  FURNITURE: 'Furniture & Fixtures',
  INTANGIBLE: 'Intangible Assets',
};

// ---------------------------------------------------------------------------
// Disposal
// ---------------------------------------------------------------------------

export const DISPOSAL_TYPE_LABEL: Record<DisposalType, string> = {
  SALE: 'Sale',
  SCRAP: 'Scrap',
  WRITE_OFF: 'Write Off',
  TRANSFER: 'Transfer',
};

// ---------------------------------------------------------------------------
// Decorative identity tint — deterministic by category, NOT a data encoding.
// Distinct from chartTokens' CVD-validated series palette (reserved for
// charts) and from the status tones above (reserved for state).
// ---------------------------------------------------------------------------

const CATEGORY_TINTS = ['#0284c7', '#7c3aed', '#16a34a', '#ea580c', '#c026d3', '#0d9488', '#65a30d', '#dc2626'];

export const tintForCategory = (categoryId: number | null | undefined, categoryName?: string | null): string => {
  const key = String(categoryId ?? categoryName ?? 'uncategorised');
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return CATEGORY_TINTS[hash % CATEGORY_TINTS.length];
};

// ---------------------------------------------------------------------------
// Financial year helpers — mirrors erp-api/src/utils/financialYear.js so the
// UI can build the same `FY2025-26` strings the API expects, and constrain
// the Income Tax period picker to a real FY without ever round-tripping a
// guess through the server first.
// ---------------------------------------------------------------------------

export const financialYearForDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed
  const startYear = month >= 3 ? year : year - 1;
  return `FY${startYear}-${String(startYear + 1).slice(-2)}`;
};

export const currentFinancialYear = (): string => financialYearForDate(new Date());

/** { startISO, endISO } for 'FY2025-26' -> 2025-04-01 .. 2026-03-31. */
export const financialYearDates = (fy: string): { startISO: string; endISO: string } => {
  const match = fy.match(/^FY(\d{4})-(\d{2})$/);
  if (!match) return { startISO: '', endISO: '' };
  const startYear = parseInt(match[1], 10);
  return { startISO: `${startYear}-04-01`, endISO: `${startYear + 1}-03-31` };
};

/** Financial year option list for a dropdown — most recent first. */
export const financialYearOptions = (yearsBack = 5, yearsForward = 1): string[] => {
  const current = currentFinancialYear();
  const match = current.match(/^FY(\d{4})-(\d{2})$/)!;
  const currentStartYear = parseInt(match[1], 10);
  const options: string[] = [];
  for (let i = yearsForward; i >= -yearsBack; i -= 1) {
    const startYear = currentStartYear + i;
    options.push(`FY${startYear}-${String(startYear + 1).slice(-2)}`);
  }
  return options;
};
